/**
 * NNADD Configurador — a secção "De onde és?" da página inicial com o desenho
 * do configurador (sections/nnadd-configurador-hero.liquid).
 *
 * O visitante procura a terra, escolhe um resultado e a t-shirt preta passa a
 * mostrar o desenho do configurador: mapa das ruas, coordenadas, frase e
 * logótipo pequeno, a branco. O desenho é o da página do configurador
 * (`criarDesenho(...).desenharLados`, assets/nnadd-configurador-design.js), a
 * partir do estado da mesma máquina (assets/nnadd-configurador-estado.js), e
 * fica por cima da foto sem estampado na área de impressão de
 * assets/nnadd-configurador-fotos.js, como a foto ao vivo do configurador.
 * "Configurar a minha" leva ao configurador com `?lugar=…&lat=…&lng=…`
 * (`urlComLugar`, o formato que `aplicarLugarDoUrl` em
 * assets/nnadd-configurador.js lê).
 *
 * O que carrega e quando (a página inicial fica leve):
 * - ao carregar a página: só este ficheiro;
 * - na primeira pesquisa: a pesquisa (assets/nnadd-coordinates-pesquisa.js) e,
 *   em paralelo, os módulos do desenho (configurador, glifos, mapas,
 *   formatarDMS, estado, fotos);
 * - logo a seguir aos módulos do desenho: o carregador dos dados de mapa
 *   (assets/nnadd-mapas-dados.js) com os tiles e os descodificadores.
 * Todos chegam por `import()` com os URLs do CDN que o Liquid põe em `data-*`.
 *
 * Enquanto o mapa carrega, a t-shirt já mostra as coordenadas, a frase e a
 * forma do mapa a pulsar (`MAPA_EM_ESPERA` do desenho); o mapa entra com um
 * fade. Se o lugar não tiver mapa, a máquina de estado passa a "só
 * coordenadas" e a secção di-lo.
 *
 * O exemplo (2026-10-24): quando a secção está quase à vista, carrega os
 * módulos do desenho e mostra uma terra ao calhas, de qualquer país
 * (`EXEMPLO_TERRAS` do estado), com o aviso "Exemplo: …" e o campo vazio.
 * Se o visitante seguir para o configurador sem procurar, o rascunho leva o
 * exemplo e a marca de exemplo, para lá aparecer o mesmo aviso.
 *
 * O rascunho do configurador (KTD15): ao carregar em "Configurar a minha", a
 * secção escreve em `sessionStorage` o rascunho que o configurador repõe ao
 * abrir (a mesma chave e o formato `v: 2` de `rascunho()`), com o lugar, a
 * composição, o estilo e a frase sorteada daqui. Assim o configurador abre
 * com o que a t-shirt mostrava, mesmo que já houvesse um rascunho antigo com
 * outro lugar (que faria `aplicarLugarDoUrl` não escolher este). O resto de
 * um rascunho antigo (história, QR, frase escrita) fica.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

/** A peça e a cor da foto: t-shirt preta, estampado a branco (a tinta por omissão da cor). */
const PECA = 'tshirt';
const COR = 'Black';
const TAMANHO = 'M';
const COMPOSICAO = 'coordenadas_mapa';
const ESTILO = 'ruas';

/** O prefixo do rascunho do configurador (RASCUNHO_PREFIXO em assets/nnadd-configurador.js). */
const RASCUNHO_PREFIXO = 'nnadd-configurador:rascunho:v1:';

const n = (/** @type {number} */ v) => String(Number(v.toFixed(3)));

/** @param {string} modelo @param {Record<string, string>} valores */
function preencher(modelo, valores) {
  return Object.entries(valores).reduce((t, [k, v]) => t.split(`[${k}]`).join(v), String(modelo ?? ''));
}

class NnaddConfiguradorHero extends HTMLElement {
  /** @type {Record<string, string>} */ textos = {};
  /** @type {Record<string, any>} */ refs = {};
  /** @type {any} */ pesquisa = null;
  /** @type {any} */ pedidos = null;
  /** @type {{ lat: number, lng: number, local: string, detalhe: string }[]} */ resultados = [];
  /** O que o desenho precisa: `{ conf, desenho, maquina, coordinates, fotos }`. */
  /** @type {any} */ modulos = null;
  /** @type {any} */ estado = null;
  /** @type {Promise<any> | null} */ #pesquisaPromise = null;
  /** @type {Promise<any> | null} */ #modulosPromise = null;
  /** @type {Promise<any> | null} */ #carregadorPromise = null;
  /** O lugar escolhido, para ignorar respostas de mapa de um lugar anterior. */
  #pedidoEscolha = 0;
  /** Se a t-shirt mostra o exemplo (e não uma terra que o visitante escolheu). */
  exemplo = false;
  /** @type {IntersectionObserver | null} */ #observador = null;
  #ultimaPrevia = '';
  #iniciado = false;

  connectedCallback() {
    if (this.#iniciado) return;
    this.#iniciado = true;

    for (const el of this.querySelectorAll('[data-ref]')) {
      this.refs[/** @type {HTMLElement} */ (el).dataset.ref ?? ''] = el;
    }
    try {
      this.textos = JSON.parse(this.querySelector('script[data-textos]')?.textContent ?? '{}');
    } catch {
      this.textos = {};
    }

    const { formulario, campo, lista, cta } = this.refs;
    formulario.addEventListener('submit', this.aoEnviar);
    campo.addEventListener('keydown', this.aoTeclarCampo);
    campo.addEventListener('input', this.cancelarPesquisa);
    lista.addEventListener('click', this.aoClicarResultado);
    lista.addEventListener('keydown', this.aoTeclarLista);
    cta.addEventListener('click', this.aoAbrirConfigurador);

    // O exemplo carrega quando a secção está quase à vista, para a página inicial ficar leve.
    if ('IntersectionObserver' in window) {
      this.#observador = new IntersectionObserver(
        (entradas) => {
          if (!entradas.some((en) => en.isIntersecting)) return;
          this.#observador?.disconnect();
          this.#observador = null;
          void this.mostrarExemplo();
        },
        { rootMargin: '300px 0px' }
      );
      this.#observador.observe(this);
    } else {
      void this.mostrarExemplo();
    }
  }

  disconnectedCallback() {
    this.pedidos?.cancelar();
    this.#observador?.disconnect();
  }

  /** Mostra uma terra de exemplo, se o visitante ainda não escolheu nenhuma. */
  async mostrarExemplo() {
    if (this.#pedidoEscolha) return;
    const mods = this.modulos ?? (await this.carregarModulos());
    if (!mods || this.#pedidoEscolha) return;
    const [lugar] = mods.modEstado.rascunhoDeExemplo().lugares;
    await this.escolher({ ...lugar, detalhe: '' }, true);
  }

  get lingua() {
    return this.dataset.lingua || document.documentElement.lang || 'en';
  }

  /* ----- módulos ----- */

  /** @returns {Promise<any>} o módulo de pesquisa, ou null se não carregar */
  carregarPesquisa() {
    if (!this.#pesquisaPromise) {
      const url = this.dataset.pesquisa;
      this.#pesquisaPromise = (url ? import(url) : Promise.reject(new Error('Falta data-pesquisa')))
        .then((modulo) => {
          this.pesquisa = modulo;
          this.pedidos = new modulo.PesquisaLugares();
          return modulo;
        })
        .catch(() => {
          this.#pesquisaPromise = null;
          return null;
        });
    }
    return this.#pesquisaPromise;
  }

  /**
   * Os módulos do desenho, como em `iniciar()` de assets/nnadd-configurador.js, e o estado
   * inicial da t-shirt preta com "coordenadas + mapa" em 'ruas'. Null se não carregarem.
   * @returns {Promise<any>}
   */
  carregarModulos() {
    if (!this.#modulosPromise) {
      const d = this.dataset;
      this.#modulosPromise = Promise.all(
        [d.desenho, d.glifos, d.mapas, d.coordinates, d.estado, d.fotos].map((url) => {
          if (!url) throw new Error('Falta um URL de módulo');
          return import(url);
        })
      )
        .then(([conf, glifos, mapas, coordinates, modEstado, fotos]) => {
          const desenho = conf.criarDesenho({ mapas, glifos, formatarDMS: coordinates.formatarDMS, logotipo: coordinates.LOGOTIPO });
          const maquina = modEstado.criarEstado({ conf, desenho, glifos, mapas, formatarDMS: coordinates.formatarDMS, lingua: this.lingua });
          let e = maquina.inicial({ peca: PECA, cor: COR, tamanho: TAMANHO });
          e = maquina.aplicar(e, { tipo: 'composicao', composicao: COMPOSICAO }).estado;
          e = maquina.aplicar(e, { tipo: 'estilo', estilo: ESTILO }).estado;
          this.estado = e;
          this.modulos = { conf, desenho, maquina, coordinates, fotos, modEstado };
          // Os ficheiros do mapa a seguir, sem esperar pela escolha (o exemplo precisa deles já).
          this.carregador().catch(() => {});
          return this.modulos;
        })
        .catch((erro) => {
          console.error('[nnadd-configurador-hero] Os módulos não carregaram:', erro);
          this.#modulosPromise = null;
          return null;
        });
    }
    return this.#modulosPromise;
  }

  /** O carregador dos dados de mapa (assets/nnadd-mapas-dados.js), só quando é preciso. */
  carregador() {
    if (!this.#carregadorPromise) {
      const d = this.dataset;
      this.#carregadorPromise = (async () => {
        if (!d.mapasDados) throw new Error('Falta data-mapas-dados');
        const m = await import(d.mapasDados);
        const modulos = await m.importarModulos({ tiles: d.mapasTiles, vectorTile: d.vendorVectorTile, png: d.vendorPng });
        return m.criarCarregador(modulos);
      })().catch((erro) => {
        this.#carregadorPromise = null;
        throw erro;
      });
    }
    return this.#carregadorPromise;
  }

  /* ----- pesquisa (como em assets/nnadd-coordinates-hero.js) ----- */

  aoEnviar = (/** @type {SubmitEvent} */ evento) => {
    // Com JS, Procurar e Enter pesquisam aqui; sem JS, o formulário leva ao configurador.
    evento.preventDefault();
    // Os módulos do desenho vêm enquanto a pesquisa corre.
    void this.carregarModulos();
    this.pesquisar();
  };

  aoTeclarCampo = (/** @type {KeyboardEvent} */ evento) => {
    if (evento.key === 'ArrowDown' && !this.refs.resultados.hidden) {
      evento.preventDefault();
      this.botoesResultado()[0]?.focus();
    } else if (evento.key === 'Escape' && !this.refs.resultados.hidden) {
      this.esconderResultados();
    }
  };

  consultaAtual() {
    return this.refs.campo.value.trim().replace(/\s+/g, ' ');
  }

  /** O texto mudou ou saiu-se: a pesquisa em curso já não interessa. */
  cancelarPesquisa = () => {
    if (!this.pedidos?.cancelar()) return;
    this.terminarOcupado();
    this.mostrarEstado('');
  };

  terminarOcupado() {
    this.refs.campo.removeAttribute('aria-busy');
    this.classList.remove('is-a-procurar');
  }

  pesquisar = async () => {
    const { campo } = this.refs;
    if (this.consultaAtual().length < 2) {
      this.cancelarPesquisa();
      this.mostrarEstado(this.textos.curto);
      campo.focus();
      return;
    }
    const modulo = this.pesquisa ?? (await this.carregarPesquisa());
    if (!modulo) {
      this.mostrarEstado(this.textos.erroCarregar);
      return;
    }
    const consulta = this.consultaAtual();
    if (consulta.length < 2) return;

    // A mesma consulta enquanto o pedido corre: um só pedido (política do Nominatim).
    if (this.pedidos.emCurso(modulo.chavePesquisa(this.lingua, consulta))) return;
    this.cancelarPesquisa();

    const resultado = await this.pedidos.procurar(consulta, this.lingua, {
      aoPedir: () => {
        this.esconderResultados();
        campo.setAttribute('aria-busy', 'true');
        this.classList.add('is-a-procurar');
        this.mostrarEstado(this.textos.aProcurar);
      },
      aoTerminar: () => this.terminarOcupado(),
      valida: () => this.consultaAtual() === consulta,
    });
    if (!resultado) return;
    if ('erro' in resultado) {
      this.esconderResultados();
      this.mostrarEstado(this.textos.erroRede);
      return;
    }
    this.mostrarResultados(resultado.lista);
  };

  /** @param {{ lat: number, lng: number, local: string, detalhe: string }[]} lista */
  mostrarResultados(lista) {
    this.resultados = lista;
    const { resultados, lista: ul } = this.refs;
    if (!lista.length) {
      ul.replaceChildren();
      resultados.hidden = true;
      this.mostrarEstado(this.textos.semResultados);
      return;
    }
    this.pesquisa.renderizarResultados(ul, lista, 'nnadd-hero');
    resultados.hidden = false;
    this.mostrarEstado((this.textos.nResultados ?? '').replace('[n]', String(lista.length)));
  }

  esconderResultados() {
    this.refs.resultados.hidden = true;
    this.refs.lista.replaceChildren();
  }

  botoesResultado() {
    return /** @type {HTMLButtonElement[]} */ ([...this.refs.lista.querySelectorAll('button')]);
  }

  aoClicarResultado = (/** @type {MouseEvent} */ evento) => {
    const item = this.pesquisa?.lugarDoClique(evento, this.resultados);
    if (item) void this.escolher(item);
  };

  aoTeclarLista = (/** @type {KeyboardEvent} */ evento) => {
    this.pesquisa?.teclarLista(evento, this.refs.lista, this.refs.campo, () => this.esconderResultados());
  };

  /** @param {string | undefined} texto */
  mostrarEstado(texto) {
    this.refs.estado.textContent = texto ?? '';
  }

  /* ----- escolha ----- */

  /**
   * @param {{ lat: number, lng: number, local: string }} item
   * @param {boolean} [exemplo] a terra de exemplo: o campo fica vazio, e o foco e o scroll não mexem
   */
  async escolher(item, exemplo = false) {
    const { campo, cta } = this.refs;
    if (!exemplo) {
      this.cancelarPesquisa();
      campo.value = item.local;
      this.esconderResultados();
    }
    const pedido = ++this.#pedidoEscolha;
    this.classList.add('is-a-desenhar');
    this.refs.aDesenhar.hidden = false;

    const mods = this.modulos ?? (await this.carregarModulos());
    if (pedido !== this.#pedidoEscolha) return;
    if (!mods) {
      this.classList.remove('is-a-desenhar');
      this.refs.aDesenhar.hidden = true;
      this.mostrarEstado(this.textos.erroCarregar);
      return;
    }
    const { arredondar6, formatarDMS } = mods.coordinates;
    const lugar = { lat: arredondar6(item.lat), lng: arredondar6(item.lng), local: item.local };
    this.estado = mods.maquina.aplicar(this.estado, { tipo: 'lugar', indice: 0, lugar }).estado;
    this.exemplo = exemplo;

    const { latitude, longitude, texto } = formatarDMS(lugar.lat, lugar.lng);
    this.refs.local.textContent = lugar.local;
    this.refs.dms.textContent = `${latitude}\n${longitude}`;
    this.refs.coordenadas.hidden = false;
    // O URL só leva o lugar se o configurador o aceitar (`lugarDoUrl`); o exemplo vai só no rascunho.
    const base = this.dataset.produtoUrl || cta.getAttribute('href') || '/';
    cta.href = this.pesquisa && !exemplo ? this.pesquisa.urlComLugar(base, lugar) : base;
    this.classList.add('tem-lugar');
    this.mostrarEstado(preencher(exemplo ? this.textos.exemplo : this.textos.escolhido, { l: lugar.local, c: texto }));
    this.renderizar();
    void this.carregarMapa(pedido);
    if (exemplo) return;

    // A lista que tinha o foco desapareceu: o foco passa para o botão, e o
    // botão fica à vista (no telemóvel pode ficar abaixo do ecrã).
    cta.focus({ preventScroll: true });
    const caixa = cta.getBoundingClientRect();
    if (caixa.top < 0 || caixa.bottom > window.innerHeight) {
      const reduzir = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      cta.scrollIntoView({ block: 'nearest', behavior: reduzir ? 'auto' : 'smooth' });
    }
  }

  /** Carrega o mapa que falta para o lugar escolhido (como `talvezCarregarMapa` no configurador). */
  async carregarMapa(/** @type {number} */ pedido) {
    const m = this.modulos?.maquina;
    const p = m?.pedidosCarga(this.estado)[0];
    if (!p) return;
    this.estado = m.aplicar(this.estado, { tipo: 'mapa_a_carregar', chave: p.chave }).estado;
    this.renderizar();
    /** @type {any} */
    let carga;
    try {
      const c = await this.carregador();
      carga = await c.carregar(p.pedido);
    } catch (erro) {
      console.warn('[nnadd-configurador-hero] O mapa não carregou:', erro);
      carga = { ok: false, motivo: 'rede' };
    }
    if (!this.isConnected || pedido !== this.#pedidoEscolha) return;
    const r = m.aplicar(this.estado, { tipo: 'mapa_carregado', chave: p.chave, carga });
    this.estado = r.estado;
    // Sem mapa, a máquina passa a "só coordenadas" com um aviso na composição: o serviço falhou
    // ('mapa_falhou') ou o lugar não tem mapa (os outros motivos).
    const aviso = r.avisos.find((/** @type {any} */ a) => a.campo === 'composicao');
    if (aviso) this.mostrarEstado(aviso.motivo === 'mapa_falhou' ? this.textos.mapaFalhou : this.textos.semMapa);
    this.renderizar();
  }

  /* ----- pré-visualização ----- */

  renderizar() {
    const mods = this.modulos;
    const e = this.estado;
    if (!mods || !e?.lugares[0]) return;
    const { maquina: m, desenho, conf, fotos } = mods;
    const estadoMapa = m.estadoMapa(e);
    const comMapa = m.comMapa(e.composicao);
    const pronto = comMapa && estadoMapa.estado === 'pronto';
    const aCarregar = comMapa && !pronto;
    const chave = JSON.stringify([e.lugares[0], e.composicao, e.estilo, e.frase, estadoMapa.estado]);
    if (chave === this.#ultimaPrevia) return;
    this.#ultimaPrevia = chave;

    // Enquanto o mapa carrega, a forma do mapa a pulsar (`MAPA_EM_ESPERA`), com as coordenadas e a frase.
    const d = m.descritor(e);
    if (!m.fraseInfo(e).valida) d.frase = '';
    const mapasLados = aCarregar ? { frente: conf.MAPA_EM_ESPERA } : m.mapasDaPrevia(e);
    let svgTexto = '';
    try {
      svgTexto = desenho.desenharLados(d, mapasLados, { pecasAtivas: Object.keys(m.pecas) }).frente;
    } catch (erro) {
      console.error('[nnadd-configurador-hero] O desenho falhou:', erro);
    }

    const { desenho: alvo, aDesenhar, atribuicao } = this.refs;
    this.classList.remove('is-a-desenhar');
    // O mapa chegou: um fade curto (a classe sai logo, para a troca seguinte não piscar).
    if (this.classList.contains('is-a-carregar-mapa') && pronto) {
      this.classList.add('is-a-entrar');
      setTimeout(() => this.classList.remove('is-a-entrar'), 400);
    }
    this.classList.toggle('is-a-carregar-mapa', aCarregar);
    aDesenhar.hidden = !aCarregar;
    atribuicao.hidden = !pronto;
    if (!svgTexto) {
      alvo.hidden = true;
      this.mostrarEstado(this.textos.erroCarregar);
      return;
    }

    // Centrado na área de impressão da foto, a TOPO_POL do topo e com a largura real (viewBox em mm).
    const area = fotos.AREAS?.[PECA]?.[COR];
    const larguraPol = conf.PECAS?.[PECA]?.area?.larguraPol;
    const vb = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(svgTexto);
    if (!area || !larguraPol || !vb) {
      alvo.hidden = true;
      return;
    }
    const pctPorPol = area.width / larguraPol;
    const largura = (Number(vb[1]) / 25.4) * pctPorPol;
    alvo.style.left = `${n(area.left + (area.width - largura) / 2)}%`;
    alvo.style.top = `${n(area.top + (fotos.TOPO_POL ?? 0) * pctPorPol)}%`;
    alvo.style.width = `${n(largura)}%`;

    const doc = new DOMParser().parseFromString(svgTexto, 'image/svg+xml');
    const svg = /** @type {SVGSVGElement} */ (document.importNode(doc.documentElement, true));
    // O tamanho vem da caixa e a altura do viewBox; os mm do ficheiro impresso não interessam aqui.
    svg.removeAttribute('width');
    svg.removeAttribute('height');
    svg.setAttribute('class', 'nnadd-hero__svg');
    svg.setAttribute('role', 'img');
    svg.setAttribute('focusable', 'false');
    const idTitulo = `${this.refs.campo.id}-previa`;
    svg.setAttribute('aria-labelledby', idTitulo);
    const titulo = document.createElementNS(SVG_NS, 'title');
    titulo.id = idTitulo;
    const lugar = e.lugares[0];
    titulo.textContent = preencher(pronto ? this.textos.previaCom : this.textos.previaSemMapa, {
      l: lugar.local,
      c: mods.coordinates.formatarDMS(lugar.lat, lugar.lng).texto,
      f: d.frase ?? '',
    });
    svg.prepend(titulo);
    alvo.replaceChildren(svg);
    alvo.hidden = false;
    this.refs.frase.textContent = d.frase ? `«${d.frase}»` : '';
    this.refs.frase.hidden = !d.frase;
  }

  /* ----- passagem para o configurador ----- */

  /**
   * Antes de seguir para o configurador, escreve o rascunho que ele repõe ao abrir (ver o
   * cabeçalho). Sem lugar, sem id do produto ou sem `sessionStorage`, segue só com o URL.
   */
  aoAbrirConfigurador = () => {
    const e = this.estado;
    const id = this.dataset.produtoId;
    if (!e?.lugares[0] || !id) return;
    try {
      const chave = `${RASCUNHO_PREFIXO}${id}`;
      /** @type {any} */
      let antigo = null;
      try {
        antigo = JSON.parse(sessionStorage.getItem(chave) ?? 'null');
      } catch {
        antigo = null;
      }
      const base = antigo && typeof antigo === 'object' && antigo.v === 2 ? antigo : { v: 2 };
      const lugares = Array.isArray(base.lugares) ? [...base.lugares] : [null, null];
      lugares[0] = e.lugares[0];
      /** @type {any} */
      const rascunho = { ...base, v: 2, lugares, composicao: e.composicao, estilo: e.estilo };
      // A frase sorteada aqui, a menos que o cliente já tenha escrito a sua no configurador.
      if (base.fraseMudada !== true) {
        rascunho.fraseIndice = e.fraseIndice ?? null;
        rascunho.fraseMudada = false;
        rascunho.frase = e.frase;
      }
      sessionStorage.setItem(chave, JSON.stringify(rascunho));
      // A marca de exemplo do configurador (`marcarExemplo`): com ela, lá aparece o mesmo aviso.
      if (this.exemplo) sessionStorage.setItem(`${chave}:exemplo`, `${e.lugares[0].lat},${e.lugares[0].lng}`);
      else sessionStorage.removeItem(`${chave}:exemplo`);
    } catch {
      // Sem sessionStorage: o configurador escolhe o lugar pelo URL.
    }
  };
}

if (!customElements.get('nnadd-configurador-hero')) {
  customElements.define('nnadd-configurador-hero', NnaddConfiguradorHero);
}
